import { useEffect, useRef } from 'react';
import { assets } from '../assets';
import { env } from '../config/env';
import { timing } from '../config';
import { noteError, noteEvent } from '../lib/diagnostics';
import { createLumaCompositor, type LumaCompositor } from './lumaComposite';

/**
 * The transformation transition.
 *
 * We play the MP4 over the live AR view and call `onOcclusion()` at the moment
 * the frame is fully covered — that is where the flow flips the costume on. The
 * AR module knows nothing about any of this; it just gets told the costume is
 * visible now.
 *
 * Two compositing paths, chosen by VITE_TRANSITION_MODE:
 *
 *   luma (default) — the MP4 is side-by-side colour + luma matte and must be
 *     keyed. Goes through a WebGL pass (see lumaComposite.ts). This is what was
 *     delivered, so it is what we ship unless told otherwise.
 *   opaque — the MP4 is a full-frame wipe. Plain <video> on top, no shader,
 *     cheapest possible thing.
 *
 * The two look identical in code and completely different on screen, and the
 * frame itself says which one is right — so the mode is checked against the
 * file's own shape once its metadata lands. See checkPackingMatchesMode.
 *
 * `timing.occlusionAtMs` is no longer a placeholder: it is measured off the
 * alpha plane of the shipped clip (2026-09-16), and it has to be re-measured
 * any time that clip is re-derived — if it is wrong the costume pop will be
 * visible, which is the one thing this whole mechanism exists to prevent.
 * scripts/build-art.mjs prints the peak it built to; the two must agree.
 */
export function TransitionOverlay({
  onOcclusion,
  onComplete,
  onSourceReady,
  media = true,
}: {
  onOcclusion: () => void;
  onComplete: () => void;
  /**
   * Hands up whichever element is actually showing the transition — the <video>
   * on the opaque path, the keyed canvas on the luma path — so the recording
   * compositor can sample it. Called with null on teardown.
   */
  onSourceReady?: (source: HTMLVideoElement | HTMLCanvasElement | null) => void;
  /**
   * Whether this device draws the transition at all. False when the AR is
   * rendered elsewhere and the fog arrives inside the stream: the timers below
   * still run so the flow is identical, but no <video>, no WebGL context and
   * no compositor overlay are created here. See AppConfig.transitionOnPanel.
   */
  media?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const occludedRef = useRef(false);
  const completedRef = useRef(false);

  const luma = env.transitionMode === 'luma';

  // Register the visible element for recording, and unregister on the way out
  // so the compositor is never left drawing a detached node.
  useEffect(() => {
    if (!onSourceReady || !media) return;
    onSourceReady(luma ? canvasRef.current : videoRef.current);
    return () => onSourceReady(null);
  }, [luma, media, onSourceReady]);

  // Occlusion and completion are driven by timers rather than the video's own
  // events, so a missing asset, a stalled decode, or a WebView that refuses to
  // autoplay still advances the flow. A guest must never be stranded here.
  useEffect(() => {
    noteEvent(`transition start (${media ? env.transitionMode : 'render machine'})`);

    const fireOcclusion = () => {
      if (occludedRef.current) return;
      occludedRef.current = true;
      noteEvent('transition occlusion — costume on');
      onOcclusion();
    };
    const fireComplete = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      onComplete();
    };

    const occlusionTimer = window.setTimeout(fireOcclusion, timing.occlusionAtMs);
    const completeTimer = window.setTimeout(fireComplete, timing.transitionMs);

    return () => {
      clearTimeout(occlusionTimer);
      clearTimeout(completeTimer);
      // If we are torn down early (idle reset, error), the costume flip must
      // still have happened or not — never half. The flow owns that state.
    };
  }, [onOcclusion, onComplete, media]);

  // Luma path: pump the video through the shader each frame.
  useEffect(() => {
    if (!luma || !media) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // A lost context mid-transition would leave the compositor sampling a blank
    // canvas — and the recording with it. Named in the admin log if it happens.
    const onLost = (e: Event): void => {
      e.preventDefault();
      noteError('transition.luma', 'WebGL context lost during the transition');
    };
    canvas.addEventListener('webglcontextlost', onLost);

    let compositor: LumaCompositor | null = null;
    try {
      compositor = createLumaCompositor(canvas);
    } catch (err) {
      // Falls back to showing nothing rather than a broken frame; the timers
      // still carry the flow through.
      noteError('transition.luma', err);
      canvas.removeEventListener('webglcontextlost', onLost);
      return;
    }

    let raf = 0;
    const loop = () => {
      compositor?.render(video);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      compositor?.destroy();
      canvas.removeEventListener('webglcontextlost', onLost);
    };
  }, [luma, media]);

  /**
   * Does the file's shape agree with the mode we were told to use?
   *
   * Getting this wrong is silent and looks like an art problem rather than a
   * config one: keying an unpacked file shows only its left half, and playing a
   * packed one raw shows both panels at once with a hard seam down the middle
   * of the frame. The frame's own aspect settles it — a side-by-side pair of
   * portrait panels is landscape, a single portrait wipe is not — so a
   * mismatched build says so in the admin log instead of reaching a guest
   * unannounced.
   */
  const checkPackingMatchesMode = (): void => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video.videoHeight) return;
    const packed = video.videoWidth > video.videoHeight;
    if (packed === luma) return;
    noteError(
      'transition.mode',
      `${video.videoWidth}x${video.videoHeight} looks ${
        packed ? 'side-by-side (needs luma)' : 'full-frame (needs opaque)'
      } but VITE_TRANSITION_MODE resolved to ${env.transitionMode}`,
    );
  };

  // The render machine is drawing the fog into the stream; nothing to add here.
  if (!media) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-30">
      <video
        ref={videoRef}
        src={assets.transition}
        muted
        autoPlay
        playsInline
        preload="auto"
        onLoadedMetadata={checkPackingMatchesMode}
        onError={(e) =>
          noteError(
            'transition.video',
            // Named, because the likeliest cause by far is that the MP4 has not
            // been delivered yet. The timers carry the flow regardless.
            `${assets.transition}: ${(e.currentTarget as HTMLVideoElement).error?.message || 'failed to load'}`,
          )
        }
        className={
          luma
            ? 'pointer-events-none absolute h-px w-px opacity-0'
            : 'h-full w-full object-cover'
        }
      />
      {luma ? <canvas ref={canvasRef} className="h-full w-full object-cover" /> : null}
    </div>
  );
}
